import { useCallback, useEffect, useState } from "react";
import { http } from "../services/http";
import { ALL_TYPES, DEFAULT_STATUS } from "../domain/projects";
import { DEFAULT_PAGE_SIZE } from "../lib/pagination";

export const PAGE_SIZE = DEFAULT_PAGE_SIZE;
const FILTER_VALUE_SEPARATOR = "|";

function readProjects(payload) {
  if (Array.isArray(payload?.data)) return payload.data;
  return payload?.data?.projects || [];
}

function readTotal(payload, projects) {
  const total = payload?.data?.total;
  return typeof total === "number" ? total : projects.length;
}

export function useProjects({
  page,
  pageSize = PAGE_SIZE,
  search = "",
  type = ALL_TYPES,
  status = DEFAULT_STATUS,
  situations = [],
  coordinators = [],
}) {
  const [projects, setProjects] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  const coordinatorsParam = coordinators.join(FILTER_VALUE_SEPARATOR);
  const situationsParam = situations.join(FILTER_VALUE_SEPARATOR);

  useEffect(() => {
    const controller = new AbortController();

    const params = { offset: (page - 1) * pageSize, limit: pageSize };
    if (search) params.search = search;
    if (type && type !== ALL_TYPES) params.type = type;
    if (status && status !== DEFAULT_STATUS) params.status = status;
    if (situationsParam) params.situations = situationsParam;
    if (coordinatorsParam) params.coordinators = coordinatorsParam;

    setLoading(true);
    setError(null);

    http
      .get("/projects", { params, signal: controller.signal })
      .then((response) => {
        if (controller.signal.aborted) return;
        const received = readProjects(response.data);
        setProjects(received);
        setTotal(readTotal(response.data, received));
        setLoading(false);
      })
      .catch((requestError) => {
        if (controller.signal.aborted) return;
        setError(requestError);
        setProjects([]);
        setTotal(0);
        setLoading(false);
      });

    return () => controller.abort();
  }, [page, pageSize, search, type, status, situationsParam, coordinatorsParam, reloadToken]);

  return { projects, total, loading, error, reload };
}
